import { createFileRoute, Link } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { TcpPortPiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'
import { CodeBlock, ExtraSection } from '@/components/feature-extra'

export const Route = createFileRoute('/_site/for/developers')({
  component: DevelopersPage,
  head: () => ({
    meta: [
      { title: 'Uptime Monitoring for Developers — API, CLI & MCP | Uptrack' },
      { name: 'description', content: 'Developer-first uptime monitoring: REST API, monitors-as-code CLI, MCP for AI assistants, webhooks, OpenAPI spec. From $3/mo.' },
      { property: 'og:title', content: 'Uptime Monitoring for Developers — API, CLI & MCP | Uptrack' },
      { property: 'og:description', content: 'REST API, CLI, MCP integration, monitoring-as-code. Built for developers who ship fast. Free plan, or paid plans from $3/mo.' },
      { property: 'og:image', content: 'https://uptrack.app/og/for-developers.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/for/developers' }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'For Developers', item: 'https://uptrack.app/for/developers' },
          ],
        }),
      },
    ],
  }),
})

const curlExample = `curl -X POST https://api.uptrack.app/api/monitors \\
  -H "Authorization: Bearer $UPTRACK_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "API health",
    "url": "https://myapp.com/health",
    "monitor_type": "http",
    "interval": 30
  }'`

const cliExample = `# install (macOS / Linux)
curl -fsSL https://raw.githubusercontent.com/Uptrack-App/uptrack-cli/main/install.sh | sh

export UPTRACK_API_KEY=utk_…
uptrack export -o uptrack.yaml   # snapshot what you have
uptrack apply  -f uptrack.yaml   # create/update to match (--dry-run to preview)`

function DevelopersPage() {
  return (
    <FeaturePage
      section="Developer monitoring tool"
      eyebrow="For · Developer monitoring tool"
      title="Built for developers who ship fast."
      intro="REST API on every paid plan. A monitors-as-code CLI. MCP for Claude and ChatGPT. AI-authored browser checks. Webhooks and an OpenAPI spec. No GUI required."
      featuresHeading="Everything you need in your workflow"
      features={[
        { title: 'REST API on paid plans', desc: 'Create, update, and delete monitors programmatically. API keys come with every paid plan, from $3/mo.' },
        { title: 'Monitors-as-code CLI', desc: 'One static binary. Define monitors in YAML, `uptrack apply` from CI, review changes in a PR.' },
        { title: 'MCP integration', desc: 'Connect Uptrack to Claude or ChatGPT via MCP. Ask your AI to check status or create monitors.' },
        { title: 'AI-authored checks', desc: 'Paste a URL and AI drafts a Playwright browser check you can review, edit, and run. Free.' },
        { title: 'Webhook payloads', desc: 'Structured JSON on every status change. Route to Slack, Discord, PagerDuty, or your own service.' },
        { title: 'OpenAPI spec', desc: 'Full OpenAPI 3.0 spec. Generate a typed client in any language.' },
      ]}
      cta={{
        heading: '30-second checks on every plan',
        body: 'Full API access on every paid plan. Start free in the dashboard, or choose a plan for the API.',
      }}
      visual={
        <ProductFrame url="uptrack.app/dashboard/monitors">
          <TcpPortPiece />
        </ProductFrame>
      }
    >
      <ExtraSection
        title="Create a monitor in one request"
        intro="Add monitoring to your deploy script, CI pipeline, or development workflow."
      >
        <CodeBlock>{curlExample}</CodeBlock>
      </ExtraSection>

      <ExtraSection
        title="Or keep your monitors in Git"
        intro={
          <>
            The <code className="font-mono">uptrack</code> CLI is one static binary — define monitors in YAML and{' '}
            <code className="font-mono">apply</code> them from CI. Review changes in a pull request, sync on merge.
          </>
        }
      >
        <CodeBlock>{cliExample}</CodeBlock>
        <p className="mt-4 text-sm">
          <Link to="/guides/monitoring-as-code" className="font-medium text-primary hover:underline">
            Monitoring-as-code guide
          </Link>
          <span className="mx-3 text-muted-foreground">·</span>
          <a href="https://github.com/Uptrack-App/uptrack-cli" className="font-medium text-primary hover:underline">
            CLI on GitHub
          </a>
        </p>
      </ExtraSection>

      <ExtraSection title="Ship, monitor, iterate">
        <p className="text-muted-foreground">
          You deploy a new service. Your CI pipeline calls the API to create a monitor. Uptrack checks it every 30
          seconds. If it goes down, you get a webhook in Slack before your users notice. Fix it, push, move on. No
          dashboards to click through.
        </p>
      </ExtraSection>
    </FeaturePage>
  )
}
